/**
 * Load a Capacitor plugin only if the installed app actually has it.
 *
 * WHY THIS IS NEEDED, AND IT IS SPECIFIC TO HOW GROWTHSYNC SHIPS
 *
 * The phone app downloads its whole interface from growth-sync.com, so a web
 * deploy reaches every installed app within a minute. The NATIVE half does not
 * move: it is whatever build that person installed, which may be months old.
 *
 * So the web bundle and the native shell can disagree about which plugins
 * exist, and the web half is always the newer of the two. Calling a plugin the
 * installed app was never built with throws:
 *
 *     Error: "App" plugin is not implemented on android
 *
 * and because these calls live in promises, it surfaces as an UNHANDLED
 * rejection, which is worse than the thing not working.
 *
 * This bit in anger on 2026-09-11. Five plugins had been resolving to a stub in
 * the browser bundle, so nothing ever called them. The moment that was fixed and
 * the real packages loaded, every older installed app started throwing on the
 * landing page, eleven times in five minutes.
 *
 * Capacitor already answers the question, so ask it before calling:
 * `Capacitor.isPluginAvailable(name)`. A missing plugin is then a feature that
 * quietly does not run on an old build, which is what it always was, rather
 * than an error thrown at somebody trying to sign in.
 */

/** The Capacitor global, present only inside the native app. */
function capacitorGlobal():
  | { isPluginAvailable?: (name: string) => boolean; getPlatform?: () => string }
  | null {
  if (typeof window === "undefined") return null;
  return (window as { Capacitor?: ReturnType<typeof capacitorGlobal> }).Capacitor ?? null;
}

/** Running inside the iOS or Android shell, as opposed to a browser. */
export function isNativeApp(): boolean {
  const platform = capacitorGlobal()?.getPlatform?.();
  return platform === "ios" || platform === "android";
}

/**
 * True when THIS installed app carries a native implementation of the plugin.
 *
 * Fails closed on purpose. If Capacitor cannot tell us, we do not call, because
 * the cost of skipping a feature is a feature that does not run and the cost of
 * guessing wrong is an exception on somebody's screen.
 */
export function isPluginAvailable(name: string): boolean {
  const capacitor = capacitorGlobal();
  if (!capacitor?.isPluginAvailable) return false;
  try {
    return capacitor.isPluginAvailable(name) === true;
  } catch {
    return false;
  }
}

/**
 * Import a Capacitor plugin, or return null when it cannot be used here.
 *
 * `pluginName` is the name Capacitor registers natively ("App", "Preferences",
 * "StatusBar", "PushNotifications"), which is NOT always the npm package name,
 * and `load` is the dynamic import. Returns null in a browser, in an app built
 * without the plugin, or if the import itself fails.
 */
export async function loadPlugin<T>(
  pluginName: string,
  load: () => Promise<T | undefined>
): Promise<T | null> {
  if (!isNativeApp()) return null;
  if (!isPluginAvailable(pluginName)) return null;
  try {
    const plugin = await load();
    // The stub that caused all this exported `undefined` for every name, so a
    // successful import is not on its own proof of a usable plugin.
    return plugin ?? null;
  } catch {
    return null;
  }
}
